
import { useState, useEffect, useRef, useCallback } from 'react';
import './DashboardHoverEffects.css';
import salespipeline from '../assets/solutions/salespipeline.svg';
import leadgen from '../assets/solutions/leadgen.svg';
import coldemail from '../assets/solutions/coldemail.svg';
import contentsystems from '../assets/solutions/contentsystems.svg';
import email from '../assets/solutions/email.svg';
import agenticworkflows from '../assets/solutions/agenticworkflows.svg';
import documents from '../assets/solutions/documents.svg';
import rechnung from '../assets/solutions/rechnung.svg';
import companybrain from '../assets/solutions/companybrain.svg';
import analytics from '../assets/solutions/analytics.svg';
import predictiveinsights from '../assets/solutions/predictiveinsights.svg';
import automatedseo from '../assets/solutions/automatedseo.svg';
import kundenservice from '../assets/solutions/kundenservice.svg';
import support from '../assets/solutions/support.svg';
import circlecheck from '../assets/solutions/circle-check.svg';
import chart from '../assets/solutionsdash/chart.svg';
import folder from '../assets/solutionsdash/folder.svg';
import message from '../assets/solutionsdash/message.svg';
import monitor from '../assets/solutionsdash/monitor.svg';
import BLCKS_LOGO_NEW from '../assets/icons/BLCKS_LOGO_NEW.svg';
import ProductCardBg from '../assets/solutions/ProductCardBg.avif';
import fullline from '../assets/solutions/FullLineNew.svg';
import feedback from '../assets/solutions/feedback.svg';
import intake from '../assets/solutions/intake.svg';

// Utility functions for hover effect
const pointerPositionRelativeToElement = (el, e) => {
  const rect = el.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  return {
    pixels: [x, y],
    percent: [
      (x / rect.width) * 100,
      (y / rect.height) * 100
    ]
  };
};

const distanceFromCenter = (el, x, y) => {
  const rect = el.getBoundingClientRect();
  const cx = rect.width / 2;
  const cy = rect.height / 2;
  return [x - cx, y - cy];
};

const angleFromPointerEvent = (dx, dy) => {
  let angleDegrees = 0;
  if (dx !== 0 || dy !== 0) {
    const angleRadians = Math.atan2(dy, dx);
    angleDegrees = angleRadians * (180 / Math.PI) + 90;
    if (angleDegrees < 0) {
      angleDegrees += 360;
    }
  }
  return angleDegrees;
};

const closenessToEdge = (el, x, y) => {
  const rect = el.getBoundingClientRect();
  const cx = rect.width / 2;
  const cy = rect.height / 2;
  const dx = x - cx;
  const dy = y - cy;
  
  let k_x = Infinity;
  let k_y = Infinity;
  
  if (dx !== 0) k_x = cx / Math.abs(dx);
  if (dy !== 0) k_y = cy / Math.abs(dy);
  
  return Math.min(Math.max(1 / Math.min(k_x, k_y), 0), 1) * 100;
};

const Solutions_Dash = () => {
  // State to track active sidebar item
  const [activeSidebar, setActiveSidebar] = useState('Sales Block');
  const [scrollY, setScrollY] = useState(0);
  const dashboardRef = useRef(null);

  // Handle scroll effect with 3D perspective
  useEffect(() => {
    const handleScroll = () => {
      if (dashboardRef.current) {
        const rect = dashboardRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // Start animation when the dashboard enters the viewport (earlier start)
        const startPosition = viewportHeight * 0.2; // Start when 20% into viewport (increased from 0.1)
        const endPosition = viewportHeight * 0.7;   // End at 70% of viewport (increased from 0.5)
        
        // Calculate progress (0 to 1) based on scroll position
        let progress = 0;
        if (rect.top < endPosition && rect.bottom > startPosition) {
          // When in the viewport, calculate progress
          progress = Math.min(1, Math.max(0, (endPosition - rect.top) / (endPosition - startPosition)));
        } else if (rect.top >= endPosition) {
          // Before entering viewport
          progress = 0;
        } else {
          // After passing viewport
          progress = 1;
        }
        
        setScrollY(progress);
      }
    };
    
    // Set initial state to starting position
    setScrollY(0);

    window.addEventListener('scroll', handleScroll);
    // Initial check in case component is already in view
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hover effect handlers
  const handleCardMouseMove = useCallback((e) => {
    const card = e.currentTarget;
    const { pixels: [x, y], percent: [perX, perY] } = pointerPositionRelativeToElement(card, e);
    const [dx, dy] = distanceFromCenter(card, x, y);
    const angle = angleFromPointerEvent(dx, dy);
    const edge = closenessToEdge(card, x, y);

    card.style.setProperty('--pointer-x', `${perX}%`);
    card.style.setProperty('--pointer-y', `${perY}%`);
    card.style.setProperty('--pointer-°', `${angle}deg`);
    card.style.setProperty('--pointer-d', `${edge}`);
  }, []);

  const handleCardMouseLeave = useCallback((e) => {
    const card = e.currentTarget;
    card.style.setProperty('--pointer-d', '0');
  }, []);

  // Calculate transform values for the entire dashboard with staggered effect
  const getDashboardTransform = () => {
    // Start with elements transformed in 3D space (20em in front, 20deg rotation)
    const startZ = 20; // Start 20em in front in z-space
    const startRotateX = 20; // Start with 20 degree X rotation
    
    // Interpolate between start and end values based on scroll progress
    const translateZ = (1 - scrollY) * startZ;
    const rotateX = (1 - scrollY) * startRotateX;
    
    return {
      transform: `perspective(1000px) translate3d(0, 0, ${translateZ}em) rotateX(${rotateX}deg)`,
      opacity: scrollY, // Fade in as it comes into view
      transformStyle: 'preserve-3d',
      transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s ease-out',
      willChange: 'transform, opacity',
      backfaceVisibility: 'hidden',
      transformOrigin: 'center center'
    };
  };

  // Get transform for sidebar with offset
  const getSidebarTransform = () => {
    // Only apply the offset when scrolling in (scrollY < 1)
    const offsetX = scrollY < 1 ? (1 - scrollY) * 150 : 0; // Reduced from 300 to 150 for smoother slide
    const offsetZ = (1 - scrollY) * 15; // Depth effect
    
    return {
      position: 'relative',
      transform: `translate3d(${offsetX > 0 ? '-' : ''}${offsetX}px, 0, ${offsetZ}em)`,
      opacity: scrollY,
      transition: scrollY > 0.99 ? 'none' : 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s ease-out',
      willChange: 'transform, opacity',
      transformStyle: 'preserve-3d',
      zIndex: 20, // Ensure it stays above other elements
      marginLeft: scrollY < 0.01 ? '-100px' : '0', // Reduced from -200px to -100px
      transitionProperty: 'transform, opacity, margin-left',
      transitionDuration: '0.8s',
      transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
    };
  };

  // Get transform for cards with staggered effect
  const getCardTransform = (item, index) => {
    // Calculate row and column for staggered effect
    const row = Math.floor(index / 2);
    const col = index % 2;
    
    // Base offsets
    const baseX = col === 0 ? -30 : 30; // Left or right offset
    const baseZ = 10 + (row * 5); // Depth based on row
    
    // Calculate final positions
    const offsetX = (1 - scrollY) * baseX;
    const offsetZ = (1 - scrollY) * baseZ;
    
    // Stagger the delay based on position
    const delay = (row * 0.05) + (col * 0.03);
    
    return {
      transform: `translate3d(${offsetX}px, 0, ${offsetZ}em)`,
      opacity: scrollY,
      transition: `transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, opacity 0.6s ease-out ${delay}s`,
      willChange: 'transform, opacity',
      transformStyle: 'preserve-3d'
    };
  };

  // Add glow element to each card and set up hover effects
  useEffect(() => {
    const cards = document.querySelectorAll('.dashboard-card');
    
    const handleMouseMove = (e) => {
      const card = e.currentTarget;
      const { pixels: [x, y], percent: [perX, perY] } = pointerPositionRelativeToElement(card, e);
      const [dx, dy] = distanceFromCenter(card, x, y);
      const angle = angleFromPointerEvent(dx, dy);
      const edge = closenessToEdge(card, x, y);

      card.style.setProperty('--pointer-x', `${perX}%`);
      card.style.setProperty('--pointer-y', `${perY}%`);
      card.style.setProperty('--pointer-deg', `${angle}deg`);
      card.style.setProperty('--pointer-d', `${edge}`);
    };

    const handleMouseLeave = (e) => {
      const card = e.currentTarget;
      card.style.setProperty('--pointer-d', '0');
    };

    cards.forEach(card => {
      // Add glow element if not exists
      if (!card.querySelector('.glow')) {
        const glow = document.createElement('div');
        glow.className = 'glow';
        card.prepend(glow);
      }
      
      // Add event listeners
      card.addEventListener('mousemove', handleMouseMove);
      card.addEventListener('mouseleave', handleMouseLeave);
    });

    // Cleanup
    return () => {
      cards.forEach(card => {
        card.removeEventListener('mousemove', handleMouseMove);
        card.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [activeSidebar]);

  // All dashboard items by category
  const dashboardItemsByCategory = {
    'Sales Block': [
      { 
        id: 1,
        icon: "salespipeline",
        title: "KI Sales Pipeline",
        description: "Behalte den Überblick über deinen Vertriebsprozess. Unsere KI organisiert Leads, automatisiert Outreach und Follow-ups und priorisiert Chancen damit deine Pipeline gefüllt und effizient gemanagt ist.",
        features: [
          "Lead Generierung & Qualifikaton",
          "Multi-Channel Outreach",
          "Follow-Ups & Terminbuchungen",
          "KI Kampagnenoptimierung"
        ]
      },
      { 
        id: 2,
        icon: "leadgen",
        title: "KI Leadgenerierung",
        description: "Finde automatisch neue, passende Kunden ohne manuelle Recherche. Unsere KI identifiziert relevante B2B-Leads basierend auf deinem Idealprofil und liefert dir qualifizierte Kontakte direkt in deine Pipeline.",
        features: [
          "Automatische Leadrecherche",
          "Lead Enrichment",
          "Lead Scoring & Priorisierung",
          "Automatisierte Berichte"
        ]
      },
      { 
        id: 3,
        icon: "coldemail",
        title: "KI Cold Email Systems",
        description: "Starte skalierbare, personalisierte E-Mail-Kampagnen vollautomatisch. Die KI schreibt, testet und versendet Cold Emails, die konvertieren abgestimmt auf Branche, Position und Bedarf deiner Zielgruppe.",
        features: [
          "Verfassen personalisierter E-Mails",
          "A/B-Testing für Betreffzeilen und Texte",
          "Automatische Follow-ups",
          "Öffnungs- und Antwortratenoptimierung"
        ]
      },
      { 
        id: 4,
        icon: "contentsystems",
        title: "KI Content Systems",
        description: "Erstelle hochwertigen Vertriebs- und Marketing-Content in Minuten. Ob LinkedIn-Posts, Blogartikel oder Pitch-Material. Die KI liefert passenden Content für jede Phase deiner Customer Journey.",
        features: [
          "Automatische Content Erstellung",
          "Automatische Blogerstellung",
          "Dynamische Anpassung an Zielgruppen",
          "Content Performance Tracking"
        ]
      }
    ],
    'Office Block': [
      { 
        id: 5,
        icon: "email",
        title: "E-Mail Automatisierung",
        description: "Schluss mit Postfach-Chaos und manuellem Sortieren. Unsere KI kategorisiert, priorisiert und beantwortet E-Mails automatisch damit du dich auf das Wesentliche konzentrieren kannst.",
        features: [
          "Automatisches Kategorisieren und Priorisieren",
          "Intelligente E-Mail Beantwortung",
          "Intent- und Sentiment Analyse",
          "Anbindung an deine Tools"
        ]
      },
      { 
        id: 6,
        icon: "agenticworkflows",
        title: "Agentic Workflows",
        description: "Arbeitsprozesse, die sich selbst steuern. Von Auftragseingang bis Fullfillment übernimmt die KI Entscheidungen, verteilt Aufgaben intelligent und sorgt für reibungslose Abläufe in Echtzeit.",
        features: [
          "Automatisierte Auftragseingänge & Fullfillment",
          "Intelligente Zuweisung & Entscheidungen",
          "Status-Updates in Echtzeit",
          "Self-Learning-Workflows"
        ]
      },
      { 
        id: 7,
        icon: "documents",
        title: "Dokumentenmanagement",
        description: "Dokumente finden, erstellen und verwalten – ohne Aufwand. Die KI organisiert Dateien automatisch, sorgt für schnelle Suchergebnisse und hält alles revisionssicher bereit.",
        features: [
          "Automatische Klassifizierung & Ablage",
          "Volltext-Suche über alle Dateien",
          "Versionskontrolle & Freigabeprozesse",
          "Sicherheit & Compliance integriert"
        ]
      },
      { 
        id: 8,
        icon: "rechnung",
        title: "Rechnungsmanagement",
        description: "Rechnungen verarbeiten, prüfen und buchen – vollautomatisch. Die KI erkennt Belege, gleicht sie mit Bestellungen ab und übernimmt Erinnerungen sowie Mahnwesen zuverlässig.",
        features: [
          "Automatisches Erkennen von Rechnungen & Belegen",
          "Matching mit Bestellungen & Verträgen",
          "Zahlungserinnerungen und Mahnwesen",
          "Export in Buchhaltungssoftware"
        ]
      }
    ],
    'Insight Block': [
      { 
        id: 9,
        icon: "companybrain",
        title: "KI Company Brain",
        description: "Das zentrale Gedächtnis deines Unternehmens. Wissen, Dokumente und Insights werden vereint, sodass deine Mitarbeiter in Sekunden Antworten und Zusammenfassungen finden.",
        features: [
          "Einheitliche Wissensdatenbank",
          "Q&A-KI Assistent für schnelle Antworten",
          "Kontextuelle Suche & Dokumentenzusammenfassungen",
          "Automatische Reporterstellung"
        ]
      },
      { 
        id: 10,
        icon: "analytics",
        title: "KI Business Analytics",
        description: "Echtzeit-Analysen, die jeder versteht. Unsere KI aktualisiert KPIs automatisch, erkennt Trends und liefert handlungsorientierte Empfehlungen für bessere Entscheidungen.",
        features: [
          "KPI-Dashboards automatisch aktualisiert",
          "Drill-down Analysen nach Abteilungen",
          "Prognosen auf Basis historischer Daten",
          "Alerts bei Abweichungen und Trends"
        ]
      },
      { 
        id: 11,
        icon: "predictiveinsights",
        title: "KI Predictive Insights",
        description: "Entdecke Muster und Anomalien in deinen Daten sofort. Die KI analysiert Zusammenhänge, liefert Insights automatisch und zeigt dir direkt umsetzbare Empfehlungen.",
        features: [
          "Anomalienerkennung in Echtzeit",
          "Zusammenhänge und Korrelationen",
          "Automatisierte Insights",
          "Handlungsempfehlungen"
        ]
      },
      { 
        id: 12,
        icon: "automatedseo",
        title: "KI Automated SEO",
        description: "Mehr Sichtbarkeit ohne manuellen Aufwand. Die KI optimiert Keywords, Inhalte und technische Faktoren kontinuierlich, passt sich an neue Suchtrends an und verbessert so dein Ranking automatisch.",
        features: [
          "Automatisierte Wettbewerbsanalyse",
          "Markttrends in Echtzeit",
          "Kundenfeedback-Auswertung",
          "Predictive Analytics"
        ]
      }
    ],
    'Service Block': [
      { 
        id: 13,
        icon: "kundenservice",
        title: "KI Kundenservice Agent",
        description: "Dein virtueller Support rund um die Uhr. Die KI beantwortet Anfragen sofort, übernimmt wiederkehrende Aufgaben und sorgt für schnellen, freundlichen Service über alle Kanäle hinweg.",
        features: [
          "24/7 Verfügbarkeit",
          "Mehrsprachiger Support",
          "Kontextbewusste Antworten",
          "Nahtlose Übergabe an menschliche Agenten"
        ]
      },
      { 
        id: 14,
        icon: "support",
        title: "KI Supportautomatisierung",
        description: "Reduziere Tickets und steigere Lösungsgeschwindigkeit. Die KI sortiert, priorisiert und beantwortet Anfragen automatisch – für effizienteren Support und zufriedenere Kunden.",
        features: [
          "Individuelle Einführungsprozesse",
          "Interaktive Tutorials",
          "Fortschrittsverfolgung",
          "Automatisierte Erinnerungen"
        ]
      },
      { 
        id: 15,
        icon: "feedback",
        title: "KI Feedback & Umfragen",
        description: "Kundenzufriedenheit messen ohne manuelle Nacharbeit. Die KI versendet Umfragen automatisch, analysiert Antworten in Echtzeit und liefert dir direkt umsetzbare Handlungsempfehlungen.",
        features: [
          "Automatische Kategorisierung",
          "Intelligente Suche",
          "Wissenslücken erkennen",
          "Team-Kollaboration"
        ]
      },
      { 
        id: 16,
        icon: "intake",
        title: "KI HR Intake Systems",
        description: "Recruiting und Onboarding, die sich selbst organisieren. Die KI analysiert Bewerbungen, matched Profile mit Stellenanforderungen und begleitet neue Mitarbeiter automatisch durch den Startprozess.",
        features: [
          "Sentiment Analyse",
          "Themenerkennung",
          "Handlungsempfehlungen",
          "Automatisierte Berichte"
        ]
      }
    ]
  };

  // Get current dashboard items based on active sidebar
  const dashboardItems = dashboardItemsByCategory[activeSidebar] || [];

  const sidebarItems = [
    { icon: chart, label: "Sales Block" },
    { icon: monitor, label: "Office Block" },
    { icon: folder, label: "Insight Block" },
    { icon: message, label: "Service Block" }
  ];

  // FeatureItem component for the features list
  const FeatureItem = ({ text }) => (
    <div className="flex items-start gap-3 mb-3 mt-2">
      <img 
        src={circlecheck} 
        alt="Checkmark" 
        className="w-5 h-5 flex-shrink-0"
      />
      <span className="text-[13px] text-[#787C87] font-['Geist'] font-normal tracking-[0.01em]">
        {text}
      </span>
    </div>
  );

  return (
    <div className="relative bg-[#010003] text-white w-full min-h-screen py-12 px-4">
      {/* Gradient Overlay for Smooth Transition */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#010003] to-[#010003] pointer-events-none"></div>
      {/* Header Section - Outside Dashboard */}
      <div id="solutions-dash" className="max-w-[80em] mx-auto mb-12 mt-[150px]">
        <div className="mb-12">
          {/*<span className="font-['Geist'] font-light text-sm text-white tracking-[0.02em] pb-3 block">
            MEHR ALS CONSULTING
          </span>*/}
          {/*<h2 className="font-['CalSans'] font-medium text-[48px] text-white tracking-[0.02em] text-left mb-6">
            We don't just guide. We build.
          </h2>*/}
          <h2 className="font-['CalSans'] font-medium text-2xl sm:text-4xl lg:text-[48px] text-white tracking-[0.02em] text-left mb-6">
            Wir beraten nicht nur. <span className="sm:hidden"><br/></span><span className="hidden sm:inline"> </span>Wir setzen um.
          </h2>
          <p className="font-['Geist'] font-normal text-sm sm:text-base text-[#ADB0B9] max-w-[700px] pb-[54px]">
            Wir zeigen dir nicht nur, wo KI den größten Hebel für dein Business hat. Wir setzen die Lösungen auch direkt für dich um. Inhouse, maßgeschneidert und nahtlos integriert in deine bestehenden Systeme. So bekommst du nicht nur eine Roadmap, sondern echte Resultate.
          </p>
        </div>
      </div>

      {/* Dashboard Container with 3D perspective */}
      <div 
        ref={dashboardRef}
        style={{
          ...getDashboardTransform(),
          width: '80em',
          height: '52.5em',
          margin: '0 auto',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          // Start with initial transform to prevent flash of unstyled content
          transform: scrollY === 0 ? 'perspective(1000px) translate3d(0, 0, 20em) rotateX(20deg)' : getDashboardTransform().transform,
          opacity: scrollY === 0 ? 0 : scrollY,
          transition: scrollY === 0 ? 'none' : 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s ease-out'
        }}
        className="flex-shrink-0"
      >
        {/* Outer container with border wrapping both sidebar and main content */}
        <div className="relative flex h-full w-full border border-[rgba(120,124,135,0.25)] rounded-2xl p-2 overflow-visible">
          {/* Sidebar with offset animation */}
          <div 
            className="w-[240px] h-full mr-4 bg-[#0D0E12] border border-[rgba(120,124,135,0.25)] rounded-xl p-4 flex flex-col z-10"
            style={getSidebarTransform()}
          >
            {/* Header with Logo */}
            <div className="flex justify-start mb-8 px-3 pt-3">
              <img 
                src={BLCKS_LOGO_NEW} 
                alt="BLCKS Logo" 
                loading="lazy"
                className="h-7 w-auto"
              />
            </div>

            {/* Navigation */}
            <nav className="space-y-2 flex-1">
              {sidebarItems.map((item, index) => (
                <div
                  key={index}
                  onClick={() => setActiveSidebar(item.label)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    activeSidebar === item.label
                      ? 'bg-[#1a1c23] text-white' 
                      : 'text-[#666] hover:text-white hover:bg-[#151515]'
                  }`}
                >
                  <img 
                    src={item.icon} 
                    alt={item.label} 
                    loading="lazy"
                    className="w-4 h-4 text-[#666] group-hover:text-white transition-colors"
                  />
                  <span className="text-sm font-medium text-[#ADB0B9]">{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto bg-green-500 text-black text-xs px-2 py-0.5 rounded-full font-medium">
                      {item.badge}
                    </span>
                  )}
                </div>
              ))}
            </nav>

            {/* User Profile - Commented Out
            <div className="flex items-center gap-3 mt-auto">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">I</span>
              </div>
              <span className="text-[#666] text-sm">Phil</span>
            </div>
            */}
          </div>

          {/* Main content area with cards */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Search Bar */}
            <div className="flex-shrink-0 mb-6 flex justify-end pr-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-[320px] bg-[#0d0e12] border border-[rgba(120,124,135,0.25)] rounded-lg px-4 py-2 text-white text-sm placeholder-[#787c87] focus:outline-none focus:border-[#555]"
                />
              </div>
            </div>

            {/* Grid with 2x2 layout and consistent spacing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto pr-2" style={{
              height: 'calc(100% - 16px)', // Account for padding
              gridAutoRows: '1fr', // Make all rows the same height
            }}>
              {dashboardItems.map((item, index) => (
                <div 
                  key={item.id}
                  className={`dashboard-item dashboard-card ${activeSidebar === item.title ? 'active' : ''}`}
                  style={{
                    transform: getCardTransform(item, index).transform,
                    opacity: getCardTransform(item, index).opacity,
                    zIndex: getCardTransform(item, index).zIndex,
                    transition: getCardTransform(item, index).transition
                  }}
                  onMouseMove={handleCardMouseMove}
                  onMouseLeave={handleCardMouseLeave}
                >
                  {/* Background Image */}
                  <div className="absolute inset-0 w-full h-full z-0 opacity-10">
                    <img 
                      src={ProductCardBg} 
                      loading="lazy"
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Content Container */}
                  <div className="relative z-10 flex flex-col h-full">
                    {/* Centered Icon and Title Side by Side */}
                    <div className="flex justify-left items-center gap-3 mb-4">
                      <img 
                        src={{
                          salespipeline,
                          leadgen,
                          coldemail,
                          contentsystems,
                          email,
                          agenticworkflows,
                          documents,
                          rechnung,
                          companybrain,
                          analytics,
                          predictiveinsights,
                          automatedseo,
                          kundenservice,
                          support,
                          feedback,
                          intake,
                        }[item.icon]}
                        alt={item.title} 
                        loading="lazy"
                        className="w-10 h-10 flex-shrink-0"
                      />
                      <h4 className="text-white text-[18px] font-['Geist'] font-medium tracking-[0.01em] text-center">
                        {item.title}
                      </h4>
                    </div>
                    
                    {/* Left-aligned Description */}
                    <p className="text-[13px] text-[#787C87] font-['Geist'] font-normal tracking-[0.01em] leading-relaxed mb-4">
                      {item.description}
                    </p>
                    
                    <div className="mt-4 flex-grow flex flex-col">
                      {/* FullLine SVG */}
                      <div className="mb-3 w-full">
                        <img 
                          src={fullline}
                          loading="lazy"
                          alt="" 
                          className="w-full h-auto"
                        />
                      </div>
                      
                      {/* Features List */}
                      <div className="flex-grow">
                        {item.features && item.features.map((feature, index) => (
                          <FeatureItem key={index} text={feature} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Solutions_Dash;

